import React, { useEffect, useState } from 'react';
import PrivateLayout from './private';
import { Navigate, Outlet, useParams, useLocation } from 'react-router-dom';
import { setAPIEntityHeader, setAPIOrgHeader } from 'services/api';
import accessLocalStorage from 'services/localStorage';
import routeConfig from 'routes/config';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import useStore from 'state/store/hooks/useStore';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import useOrgLevel from 'utils/hooks/useOrgLevel';
import { ORG_LEVEL_PARAM, SELECTED_ENTITY_IDS_PARAM } from 'utils/constants';

const DashboardLayout = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [headersReady, setHeadersReady] = useState(false);
  const { orgId } = useParams();
  const location = useLocation();
  const isOrgLevel = useOrgLevel();
  const { toFetchActiveOrg, organisation } = useOrganisation();
  const { getMSOPermissions } = usePaymentConfig();
  const { activeOrg } = organisation;
  const { store } = useStore();
  const { storesList, error } = store;

  /**
   * `storesList` is null both before the list has loaded and after a failed
   * fetch, so its absence says nothing about whether `orgId` is valid. Only an
   * array means we actually know the set of orgs the user can reach.
   */
  const isStoresListLoaded = Array.isArray(storesList);
  const hasStoresListFailed = Boolean(error?.fetchAllStores);

  const validateOrgID = () => {
    // to handle invalid org IDs from URL
    return storesList?.find((org) => org.organization_id === orgId);
  };

  const appInit = async () => {
    if (orgId) {
      setAPIOrgHeader(orgId);
      accessLocalStorage.setOrgId(orgId);
      await toFetchActiveOrg();
      getMSOPermissions();
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    if (orgId) {
      appInit();
    }
  }, [orgId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isOrgLevelParam = searchParams.get(ORG_LEVEL_PARAM.KEY);
    const selectedEntityIdsParam = searchParams.get(SELECTED_ENTITY_IDS_PARAM.KEY);

    const isOrgLevelStorage = accessLocalStorage.getIsOrgLevel();
    const selectedEntityIdsStorage = accessLocalStorage.getSelectedEntityIds();

    if (isOrgLevelParam !== null) {
      const isOrgLevelValue = isOrgLevelParam === ORG_LEVEL_PARAM.VALUE;
      if (isOrgLevelStorage !== isOrgLevelValue) {
        accessLocalStorage.setIsOrgLevel(isOrgLevelValue);
      }
    }
    else if (isOrgLevelStorage) {
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set(ORG_LEVEL_PARAM.KEY, ORG_LEVEL_PARAM.VALUE);

      if (selectedEntityIdsStorage && selectedEntityIdsStorage.length > 0) {
        newSearchParams.set(SELECTED_ENTITY_IDS_PARAM.KEY, selectedEntityIdsStorage.join(','));
      }

      const newUrl = `${location.pathname}?${newSearchParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }

    if (selectedEntityIdsParam) {
      const entityIds = selectedEntityIdsParam.split(',').filter(id => id.trim());
      accessLocalStorage.setSelectedEntityIds(entityIds);
    } else if (isOrgLevelParam === ORG_LEVEL_PARAM.VALUE) {
      accessLocalStorage.setSelectedEntityIds([]);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (activeOrg?.treez_entity_id) {
      accessLocalStorage.setEntityId(activeOrg.treez_entity_id);
      setAPIEntityHeader(activeOrg.treez_entity_id);
      setHeadersReady(true);
    }
  }, [activeOrg]);

  // Redirect if user is in org-level view and tries to access restricted pages
  const restrictedPaths = [
    routeConfig.SETTINGS.layout,
    routeConfig.MARKETPLACE.layout,
    routeConfig.APPLICATION_CENTER.layout,
    routeConfig.VIRTUAL_TERMINALS.layout,
    routeConfig.DEVELOPERS.layout,
  ];

  const isRestrictedPath = restrictedPaths.some(path =>
    location.pathname.includes(path)
  );

  if (isOrgLevel && isRestrictedPath) {
    return (
      <Navigate
        to={`${routeConfig.ROOT}${orgId}/${routeConfig.ANALYTICS.layout}/${routeConfig.ANALYTICS.HOME.path}`}
        replace
      />
    );
  }

  // to handle invalid org IDs in URL, and to get the user somewhere useful if
  // we can't load the list at all. A pending list is *not* grounds to bounce —
  // doing that sent valid orgs to /stores/list whenever the list happened to be
  // refetching while this layout was mounted.
  const shouldRedirectToStoresList =
    !orgId || hasStoresListFailed || (isStoresListLoaded && !validateOrgID());

  if (shouldRedirectToStoresList) {
    return (
      <Navigate
        to={`${routeConfig.ROOT}${routeConfig.STORES.layout}/${routeConfig.STORES.LIST.path}`}
        replace
      />
    );
  }
  if (!isStoresListLoaded || !isLoaded || !headersReady) return null;
  return (
    <PrivateLayout>
      <Outlet />
    </PrivateLayout>
  );
};

export default DashboardLayout;
